import { format } from 'date-fns'
import moment from 'moment'
import create, { type GetState, type Mutate, type SetState, type StoreApi } from 'zustand'
import { persist } from 'zustand/middleware'

interface YearComparison {
  year1: string
  year2: string | null
}
// TODO: EC-1343 remove year1 and year2 types once not needed anymore

export type YearSettingKeys = {
  [K in keyof DataViewSettingsState]: DataViewSettingsState[K] extends YearComparison ? K : never
}[keyof DataViewSettingsState]

// declared types individually here in case we need to extend them divergently
type BaseLoadTrendType = YearComparison & { comparisonYears: string[] }
type PeakloadTrend = YearComparison & { comparisonYears: string[] }
type TotalConsumptionTrend = YearComparison & { comparisonYears: string[] }
type EmissionsTrend = YearComparison & { comparisonYears: string[] }
type EfficiencyTrend = YearComparison & { comparisonYears: string[] }

interface DataViewSettingsState {
  baseloadTrend: BaseLoadTrendType
  peakloadTrend: PeakloadTrend
  totalConsumptionTrend: TotalConsumptionTrend
  emissionsTrend: EmissionsTrend
  efficiencyTrend: EfficiencyTrend
}

interface DataViewSettingsActions {
  setDataViewSetting: (viewSetting: keyof DataViewSettingsState, key: string, value: any) => void
}

type DataViewSettingsStore = DataViewSettingsState & DataViewSettingsActions

const initialState: DataViewSettingsState = {
  baseloadTrend: {
    year1: moment().format('YYYY'),
    year2: null,
    comparisonYears: [format(new Date(), 'yyyy')],
  },
  peakloadTrend: {
    year1: moment().format('YYYY'),
    year2: null,
    comparisonYears: [format(new Date(), 'yyyy')],
  },
  totalConsumptionTrend: {
    year1: moment().format('YYYY'),
    year2: moment().subtract(1, 'year').format('YYYY'),
    comparisonYears: [moment().format('YYYY'), moment().subtract(1, 'year').format('YYYY')],
  },
  emissionsTrend: {
    year1: moment().format('YYYY'),
    year2: null,
    comparisonYears: [format(new Date(), 'yyyy')],
  },
  efficiencyTrend: {
    year1: moment().format('YYYY'),
    year2: null,
    comparisonYears: [format(new Date(), 'yyyy')],
  },
}

export const useDataViewSettingsStore = create<
  DataViewSettingsStore,
  SetState<DataViewSettingsStore>,
  GetState<DataViewSettingsStore>,
  Mutate<StoreApi<DataViewSettingsStore>, [['zustand/persist', Partial<DataViewSettingsStore>]]>
>(
  persist(
    set => ({
      ...initialState,
      setDataViewSetting: (viewSetting, key, value) =>
        set(state => ({
          ...state,
          [viewSetting]: { ...state[viewSetting], [key]: value },
        })),
    }),
    {
      name: 'data-view-settings',
      getStorage: () => localStorage,
    }
  )
)

export const filterYears = (arr: string[], max: number): string[] => {
  if (arr.length === 0) return [max.toString()]

  const filtered = arr
    .map(Number)
    .filter(num => !Number.isNaN(num) && num <= max)
    .map(String)

  return filtered.length === 0 ? [max.toString()] : filtered
}
